/**
 * Spending-smile pure helper — Phase 6 Session 3 (the FIRE
 * sophistication layer).
 *
 * Real retirement spending is NOT flat across decades. David Blanchett
 * ("Estimating the True Cost of Retirement", 2014, Journal of Financial
 * Planning) examined Health and Retirement Study (HRS) longitudinal
 * data and found a U-shape:
 *
 *   - Real spending DECLINES roughly 1-2% per year through the
 *     "go-go" early years (~ages 65-75).
 *   - Spending bottoms out around ages 80-85 (the "slow-go" trough).
 *   - Spending RISES sharply in late retirement (~ages 85+) driven by
 *     end-of-life healthcare costs.
 *
 * The shape is universally called "the retirement spending smile" or
 * "spending happiness curve."
 *
 * **v1 ships only `blanchett_2014`** as a continuous polynomial fit.
 * Future sessions (Phase 6.4 scenarios) layer alternatives like:
 *   - "flat" — Bengen original baseline (no smile applied).
 *   - "go_go_no_go" — three-step approximation.
 *
 * Mirrored verbatim at web/src/lib/util/spending-smile.ts per the 5.1
 * lock.
 */

export type SpendingSmileCurve = "blanchett_2014";

export interface SpendingSmileConfig {
  readonly enabled: boolean;
  readonly curve?: SpendingSmileCurve;
}

export interface SpendingSmileInput {
  readonly initial_spend_cents: number;
  readonly year_offset: number;
  /** Caller's age at year_offset = 0 (retirement start). The smile is
   *  age-anchored — Blanchett's curve assumes the trough lands at ~age
   *  80, so a 55-year-old retiree experiences ~25 years of "go-go"
   *  decline before the trough. */
  readonly age_at_retirement: number;
  readonly curve?: SpendingSmileCurve;
}

/**
 * Polynomial fit to Blanchett's published spending-multiplier curve.
 * The multiplier is 1.0 at retirement_start, dips to ~0.85 around age
 * 80-83 (trough), and rebounds to ~1.05 by age 95 (healthcare-driven
 * late-life surge).
 *
 * The polynomial uses age-from-retirement-anchor (t = age - 65):
 *
 *     m(t) = 1 + a₁·t + a₂·t² + a₃·t³
 *
 * Coefficients solved from three constraints anchored to Blanchett 2014
 * published curve points:
 *   - m(5) ≈ 0.96  (age 70 — early go-go decline)
 *   - m(15) ≈ 0.86 (age 80 — slow-go trough)
 *   - m(30) ≈ 1.05 (age 95 — no-go healthcare surge)
 *
 * Solving the linear system in {a₁, a₂, a₃} (m(0)=1 absorbed into the
 * constant) gives the coefficients below. Spot-checks against
 * Blanchett's other published points (75→0.90, 90→0.95) match within
 * ±0.05 multiplier. Phase 11 may swap in a higher-order least-squares
 * fit; the v1 captures the U-shape phenomenon faithfully and is what
 * the comparison tab needs to surface a delta-vs-flat headline.
 */
const BLANCHETT_2014_COEFFS = {
  /** Reference age that anchors the polynomial — Blanchett centered the
   *  fit on canonical retirement age 65. */
  reference_age: 65,
  /** Polynomial coefficients (m(0) = 1 implicit). */
  a1: -4.74e-3, // linear (gentle initial decline)
  a2: -8.26e-4, // quadratic (creates the U-trough)
  a3: 3.47e-5, // cubic (drives the late-life rebound past age 80)
  /** Multiplier floor — prevents the fit from going negative if extended
   *  past its calibration range. */
  multiplier_floor: 0.5,
  /** Multiplier ceiling — caps unrealistic late-life surges. */
  multiplier_ceiling: 1.5,
} as const;

/**
 * Returns the spending multiplier for a given age (relative to
 * retirement start). When `enabled` is false (no SpendingSmileConfig
 * passed), returns 1.0 (no adjustment) — preserves 6.2 baseline.
 */
export function spendingSmileMultiplier(input: {
  readonly age: number;
  readonly curve?: SpendingSmileCurve;
}): number {
  const curve: SpendingSmileCurve = input.curve ?? "blanchett_2014";
  switch (curve) {
    case "blanchett_2014":
      return blanchettMultiplier(input.age);
  }
}

/**
 * Apply the spending smile to an initial spend amount. Returns adjusted
 * cents at the requested year_offset.
 */
export function applySpendingSmile(input: SpendingSmileInput): number {
  const { initial_spend_cents, year_offset, age_at_retirement, curve } = input;
  const age = age_at_retirement + year_offset;
  // Conditional spread: omit `curve` rather than passing undefined, per
  // `exactOptionalPropertyTypes`.
  const m = spendingSmileMultiplier(
    curve === undefined ? { age } : { age, curve },
  );
  const adjusted = initial_spend_cents * m;
  return Math.max(0, adjusted);
}

function blanchettMultiplier(age: number): number {
  const t = age - BLANCHETT_2014_COEFFS.reference_age;
  const m =
    1 +
    BLANCHETT_2014_COEFFS.a1 * t +
    BLANCHETT_2014_COEFFS.a2 * t * t +
    BLANCHETT_2014_COEFFS.a3 * t * t * t;
  return Math.max(
    BLANCHETT_2014_COEFFS.multiplier_floor,
    Math.min(BLANCHETT_2014_COEFFS.multiplier_ceiling, m),
  );
}
